import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import type {
  ActionGroupStatus,
  Job,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import {
  getActionRecordJobCount,
  getLoopNestedRecordJobId,
  getRecordJobId,
} from "../../../shared/status-walk";
import { orderStatusGroupsByConfig } from "../../../shared/status-order";
import { ActionRenderer } from "../LoopingActionsGroup/ActionRenderer";
import type { RenderNestedGroups } from "../BranchesGroup/BranchesGroup";
import { BranchesGroup } from "../BranchesGroup/BranchesGroup";
import { LoopingBlock } from "../LoopingBlock/LoopingBlock";

interface NestedGroupsRendererProps {
  groups: ActionGroupStatus[];
  job: Job;
  recordIndex: number;
  recordJobData: Record<string, RecordJobData>;
  /**
   * `fallbackRecordJobId` is the id to try when `recordJobId` is not found:
   * which scheme an action's record jobs use is decided per EXECUTOR, not by
   * position, so only the shape is knowable here (see the branch-shape
   * fallback built below). Omitted for every shape that has no safe fallback.
   */
  onDetailsClick: (recordJobId: string, fallbackRecordJobId?: string) => void;
  /** True when `groups` execute inside a loop iteration. */
  insideLoop?: boolean;
  /**
   * True when `groups` sit under a BRANCHES block inside that loop -- the
   * shape whose record jobs live ONLY at
   * `.../parent-record-index-{iteration}/record-0` (the engine creates branch
   * actions from the iteration's record job; dev job 0bda3708 has exactly
   * those rows and no plain ones). Every action DIRECTLY in a loop's actions
   * group instead gets a PLAIN per-iteration row `record-{iteration}`
   * (hotfix jobs fa133404/637c3b54; dev job 9bf92e4c, where even the
   * update-after-find keeps plain coordinator rows and only its CHILDREN --
   * addressed via recordJobCreatedCount by WithFindWrapper -- carry the
   * parent segment). Sending direct in-loop actions to the parent path
   * fetched rows the engine never wrote, so they rendered no data
   * (STR-14248).
   */
  insideLoopBranch?: boolean;
  /**
   * The ENCLOSING loop's iteration index when `groups` execute inside a
   * NESTED loop (loop in loop) -- the second shape the engine creates from a
   * parent record job, so its actions live ONLY at
   * `.../parent-record-index-{outerIteration}/record-{innerIteration}`
   * (hotfix job 3d602f47, STR-14152: loop -> loop -> create-record wrote
   * exactly those rows and no plain ones, so the plain per-iteration id
   * rendered no data). Undefined everywhere else -- a TOP-LEVEL loop's
   * subtrees keep the plain scheme above.
   */
  loopParentRecordIndex?: number;
}

/**
 * Recurses an ActionGroupStatus[] tree (any nesting of loop / branches /
 * flat actions groups) into the matching component per group kind, threading
 * itself back in as the `renderNestedGroups` render prop so BranchesGroup and
 * LoopingBlock don't need to import this module directly (no import cycle --
 * they only import each other's *types*).
 */
export const NestedGroupsRenderer: FunctionComponent<
  NestedGroupsRendererProps
> = ({
  groups,
  job,
  recordIndex,
  recordJobData,
  onDetailsClick,
  insideLoop = false,
  insideLoopBranch = false,
  loopParentRecordIndex,
}) => {
  // Stable identity: LoopingBlock/BranchesGroup are observer(memo) components,
  // so a fresh render-prop closure per render would cascade re-renders (and
  // re-walks) through every expanded block in the subtree.
  // `nestedInsideLoop` is set by LoopingBlock (its subtrees enter a NEW loop
  // scope, so the branch flag resets -- and, when the current scope is
  // already a loop iteration, that iteration's index becomes the subtree's
  // parent-record-index; see the loopParentRecordIndex prop doc) and omitted
  // by BranchesGroup (branches inherit the current scope).
  const renderNested: RenderNestedGroups = useCallback(
    (nested, index, nestedInsideLoop) => (
      <NestedGroupsRenderer
        groups={nested}
        job={job}
        recordIndex={index}
        recordJobData={recordJobData}
        onDetailsClick={onDetailsClick}
        insideLoop={nestedInsideLoop ?? insideLoop}
        insideLoopBranch={
          nestedInsideLoop === undefined ? insideLoopBranch : false
        }
        loopParentRecordIndex={
          nestedInsideLoop === undefined
            ? loopParentRecordIndex
            : insideLoop
              ? recordIndex
              : undefined
        }
      />
    ),
    [
      job,
      recordJobData,
      onDetailsClick,
      insideLoop,
      insideLoopBranch,
      loopParentRecordIndex,
      recordIndex,
    ],
  );

  // The render prop handed to a BRANCHES block: crossing it while inside a
  // loop moves the subtree into the branch parent-record-index scheme (see
  // the insideLoopBranch prop doc), addressed by the nearest iteration's
  // index -- the `recordIndex` this subtree inherits -- so
  // loopParentRecordIndex deliberately resets. Verified for branches in a
  // single loop (dev job 0bda3708); a branch inside a NESTED loop has no
  // payload evidence yet.
  const renderNestedInBranch: RenderNestedGroups = useCallback(
    (nested, index) => (
      <NestedGroupsRenderer
        groups={nested}
        job={job}
        recordIndex={index}
        recordJobData={recordJobData}
        onDetailsClick={onDetailsClick}
        insideLoop={insideLoop}
        insideLoopBranch={insideLoop || insideLoopBranch}
      />
    ),
    [job, recordJobData, onDetailsClick, insideLoop, insideLoopBranch],
  );

  // NOT the incoming order: a nested status list is instanceId-ordered as much
  // as the top-level one, so a group inserted above an existing block inside a
  // branch/iteration would render after it (STR-14248).
  const orderedGroups = useMemo(
    () => orderStatusGroupsByConfig(groups, job.automation?.actionGroups ?? []),
    [groups, job.automation?.actionGroups],
  );

  return (
    <>
      {orderedGroups.map((group) => {
        if (group.status.oneofKind === "actions") {
          return group.status.actions.actions.map((action) => {
            // Actions under a BRANCH inside a loop live at
            // parent-record-index-{iteration}/record-0, actions in a NESTED
            // loop at parent-record-index-{outer}/record-{inner} (STR-14152);
            // everything else -- including every action directly in a
            // TOP-LEVEL loop's actions group -- has a plain per-iteration row
            // (see the two prop docs for the payload evidence; STR-14248).
            const recordJobId = insideLoopBranch
              ? getLoopNestedRecordJobId(
                  job.jobId,
                  group.instanceId,
                  action.actionReference?.instanceId,
                  recordIndex,
                )
              : loopParentRecordIndex !== undefined
                ? getLoopNestedRecordJobId(
                    job.jobId,
                    group.instanceId,
                    action.actionReference?.instanceId,
                    loopParentRecordIndex,
                    recordIndex,
                  )
                : getRecordJobId(
                    job.jobId,
                    group.instanceId,
                    action.actionReference?.instanceId,
                    recordIndex,
                  );
            // Same action, PLAIN per-iteration row: the branch shape's own
            // record index is always 0 and its iteration IS the parent index,
            // so this is the row a plain-writing executor (comment-record:
            // prod job acf186f9) leaves instead of the parent-record-index one.
            // Only this shape maps back -- a nested loop's inner iteration 0
            // builds an identical-looking id whose parent segment is the OUTER
            // iteration, and WithFindWrapper's children never come through
            // here at all.
            const fallbackRecordJobId = insideLoopBranch
              ? getRecordJobId(
                  job.jobId,
                  group.instanceId,
                  action.actionReference?.instanceId,
                  recordIndex,
                )
              : undefined;
            // A record-list action (Update/Delete over a list) fans out into
            // SIBLING record jobs record-0..record-{n-1} -- verified against
            // dev job ac5f878b, whose branch updated 8 records and produced
            // group-2/action-4/record-0..record-7. Only record-0 was rendered,
            // so Run History showed one record for all eight (BUG-21290).
            //
            // Skipped inside a loop: the engine reports one progress count per
            // ACTION, not per iteration, so the same read would give every
            // iteration the total. Those keep their single per-iteration record
            // job, and an in-loop update-after-find still fans out from its own
            // recordJobCreatedCount (see WithFindWrapper).
            const recordJobCount = insideLoop
              ? undefined
              : getActionRecordJobCount(action, job);
            const recordJobIds =
              recordJobCount !== undefined && recordJobCount > 1
                ? Array.from({ length: recordJobCount }, (_, index) =>
                    getRecordJobId(
                      job.jobId,
                      group.instanceId,
                      action.actionReference?.instanceId,
                      index,
                    ),
                  )
                : undefined;
            return (
              <ActionRenderer
                key={recordJobId}
                actionWithDetails={{ action, recordJobId, recordJobIds }}
                recordJobDataByDetailsId={recordJobData}
                recordJobData={recordJobData[recordJobId]}
                onHeaderClick={onDetailsClick}
                onDetailsClick={() =>
                  onDetailsClick(recordJobId, fallbackRecordJobId)
                }
              />
            );
          });
        }
        if (group.status.oneofKind === "loop") {
          return (
            <LoopingBlock
              key={`loop-${group.instanceId}`}
              statusGroup={group}
              job={job}
              renderNestedGroups={renderNested}
              recordJobData={recordJobData}
              fetchRecordJob={onDetailsClick}
            />
          );
        }
        if (group.status.oneofKind === "branches") {
          return (
            <BranchesGroup
              key={`branches-${group.instanceId}`}
              statusGroup={group}
              job={job}
              recordIndex={recordIndex}
              renderNestedGroups={renderNestedInBranch}
            />
          );
        }
        return null;
      })}
    </>
  );
};
